// @systeme: emargement
// ============================================================================
// PairsForm - Page publique de confirmation de présence
// ============================================================================
// Accessible via le lien dans l'email de convocation (sans authentification).
// Route : /confirmer-presence/:token
//
// Au montage : enregistre la présence en 1 clic (POST). Ensuite un bouton
// unique permet de BASCULER présent ↔ pas présent (corriger une confirmation
// faite par erreur) — l'indicateur affiche l'état courant (1 ou 0).
// ============================================================================

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, Calendar, Clock, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { formatDateJourFR } from "@cecodev/shared";

interface SeanceInfo {
  dateSeance: string;
  heureDebut: string;
  heureFin: string;
  groupeNom: string;
  projetNom: string | null;
}

export default function ConfirmationPresencePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "ok" | "expired" | "error">("loading");
  const [present, setPresent] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [seance, setSeance] = useState<SeanceInfo | null>(null);

  // Au montage : on enregistre la présence (1 clic depuis l'email).
  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }
    api
      .post("/confirmation-presence", { token })
      .then((res) => {
        setSeance(res.data.seance);
        setPresent(true);
        setStatus("ok");
      })
      .catch((err) => {
        setStatus(err.response?.status === 410 ? "expired" : "error");
      });
  }, [token]);

  // Bascule présent ↔ pas présent (corriger une confirmation par erreur).
  async function basculer() {
    if (!token) return;
    setActionLoading(true);
    try {
      const route = present ? "/confirmation-presence/absence" : "/confirmation-presence";
      const res = await api.post(route, { token });
      setSeance(res.data.seance);
      setPresent(!present);
    } catch {
      // On laisse l'état courant ; l'utilisateur peut réessayer.
    } finally {
      setActionLoading(false);
    }
  }

  if (status === "loading") {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Enregistrement en cours...
        </Typography>
      </Container>
    );
  }

  const dateFormatted = seance?.dateSeance
    ? formatDateJourFR(seance.dateSeance)
    : "";

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card sx={{ textAlign: "center" }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 4, color: "#1E4A43" }}>
            PairsForm
          </Typography>

          {status === "ok" && (
            <>
              <Box sx={{ mb: 3 }}>
                {present ? (
                  <CheckCircle size={64} color="#4BB7A5" />
                ) : (
                  <AlertTriangle size={64} color="#f59e0b" />
                )}
              </Box>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                {!present ? "Présence annulée" : "Présence confirmée !"}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {!present
                  ? "Vous n'êtes plus noté(e) comme prévu(e) à cette séance."
                  : "Merci, c'est noté. Vous signerez votre émargement à l'ouverture de la séance."}
              </Typography>

              {seance && (
                <Box sx={{ bgcolor: "grey.50", borderRadius: 2, p: 2, mb: 3, textAlign: "left" }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {seance.groupeNom}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Calendar size={16} />
                    <Typography variant="body2">{dateFormatted}</Typography>
                  </Box>
                  {seance.heureDebut && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Clock size={16} />
                      <Typography variant="body2">
                        {seance.heureDebut?.slice(0, 5)} – {seance.heureFin?.slice(0, 5)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Bascule présent ↔ pas présent : corriger une confirmation par erreur. */}
              <Button
                variant={present ? "text" : "contained"}
                color={present ? "warning" : "primary"}
                disabled={actionLoading}
                onClick={basculer}
                startIcon={actionLoading ? <CircularProgress size={14} /> : undefined}
                sx={{
                  textTransform: "none",
                  mb: 2,
                  ...(present ? {} : { bgcolor: "#4BB7A5", "&:hover": { bgcolor: "#3da393" } }),
                }}
              >
                {present
                  ? "Je me suis trompé(e) — annuler ma confirmation"
                  : "Finalement, je confirme ma présence"}
              </Button>

              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/mon-espace")}
                  sx={{ textTransform: "none" }}
                >
                  Voir mes séances
                </Button>
              </Box>
            </>
          )}

          {status === "expired" && (
            <>
              <Box sx={{ mb: 3 }}>
                <AlertTriangle size={64} color="#f59e0b" />
              </Box>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                Lien expiré
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                La confirmation de présence n'est possible qu'avant le début de la
                séance. Si la séance a eu lieu, votre présence est constatée par
                l'émargement (signature) — ce lien n'est plus utile.
              </Typography>
              <Button variant="contained" onClick={() => navigate("/login")}>
                Se connecter
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <Box sx={{ mb: 3 }}>
                <AlertTriangle size={64} color="#ef4444" />
              </Box>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                Lien invalide
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Ce lien de confirmation n'est pas valide. Vérifiez que vous avez copié le
                lien complet depuis votre email.
              </Typography>
              <Button variant="contained" onClick={() => navigate("/login")}>
                Se connecter
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
